import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Phone, MessageCircle, CheckCircle2, Wind, Droplets, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import Header from './Header';
import Footer from './Footer';
import FloatingWhatsApp from './FloatingWhatsApp';

const AssistenzaCondizionatoriRoma = () => {
  const handleCall = () => {
    window.location.href = 'tel:3287686356';
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/393287686356?text=Ciao,%20ho%20bisogno%20di%20assistenza%20condizionatori%20a%20Roma.%20Potete%20intervenire?', '_blank');
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const servizi = [
    {
      icon: Wind,
      title: 'Ricarica Gas R32',
      description: 'Ricarica gas refrigerante R32 per climatizzatori di ultima generazione. Tecnici certificati FGAS operativi H24 in tutta Roma e provincia.'
    },
    {
      icon: Droplets,
      title: 'Sanificazione Filtri',
      description: 'Pulizia professionale e sanificazione filtri condizionatore. Eliminiamo batteri, muffe e cattivi odori per un\'aria più salubre.'
    },
    {
      icon: Shield,
      title: 'Riparazione Urgente Clima H24',
      description: 'Intervento rapido per condizionatore che non raffredda, perde acqua o fa rumore. Disponibili 24/7 anche nei festivi.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Assistenza Condizionatori Roma H24 | Ricarica Gas R32 Certificati FGAS</title>
        <meta name="description" content="Assistenza condizionatori Roma H24. Ricarica gas R32, sanificazione filtri, riparazione urgente. Tecnici certificati FGAS. Intervento in 30-60 minuti. Tel: 328 768 6356" />
        <link rel="canonical" href={`${window.location.origin}/assistenza-condizionatori-roma/`} />
      </Helmet>
      
      <div className="min-h-screen">
        <Header />
        
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-50 to-orange-50 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Assistenza Condizionatori Roma H24
              </h1>
              <p className="text-xl text-gray-700 mb-8">
                Tecnici certificati FGAS per ricarica gas R32, sanificazione filtri e riparazione urgente. Interveniamo in 30-60 minuti in tutta Roma.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button
                  onClick={handleWhatsApp}
                  size="lg"
                  className="bg-orange-500 hover:bg-orange-600 text-white text-xl px-10 py-7 rounded-lg shadow-lg transition-all duration-300 hover:scale-105"
                >
                  <MessageCircle className="mr-3 h-7 w-7" />
                  Contattaci su WhatsApp
                </Button>
                
                <Button
                  onClick={handleCall}
                  size="lg"
                  className="bg-blue-700 hover:bg-blue-800 text-white text-xl px-10 py-7 rounded-lg shadow-lg transition-all duration-300 hover:scale-105"
                >
                  <Phone className="mr-3 h-7 w-7" />
                  Chiama: 328 768 6356
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Servizi Condizionatori */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
              Servizi Specializzati Climatizzazione
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {servizi.map((servizio, index) => {
                const IconComponent = servizio.icon;
                return (
                  <Card key={index} className="border-2 border-blue-100 hover:border-blue-300 transition-all">
                    <CardHeader>
                      <div className="mb-4 inline-flex p-4 bg-blue-100 rounded-full">
                        <IconComponent className="h-10 w-10 text-blue-700" />
                      </div>
                      <CardTitle className="text-xl mb-3">{servizio.title}</CardTitle>
                      <CardDescription className="text-base text-gray-700">
                        {servizio.description}
                      </CardDescription>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Certificazione FGAS */}
        <section className="py-16 bg-gradient-to-r from-blue-700 to-blue-600 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Tecnici Certificati FGAS
              </h2>
              <p className="text-xl mb-8">
                SOS Casa Roma è autorizzata per l'installazione, manutenzione e ricarica gas refrigeranti secondo normativa europea F-Gas. Tutti i nostri tecnici sono certificati e operano nel rispetto delle normative ambientali vigenti.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-6 w-6" />
                  <span>Certificazione FGAS</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-6 w-6" />
                  <span>Gas R32 e R410A</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-6 w-6" />
                  <span>Intervento H24</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Zona Operativa */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
                Zone Coperte - Assistenza Condizionatori Roma
              </h2>
              <p className="text-lg text-gray-700 text-center mb-6">
                Arriviamo in 30-60 minuti anche in zone a traffico limitato: Centro Storico, Prati, Parioli, EUR, Ostia, Tiburtina, Prenestina, Casilina, Nomentana, Aurelio, Primavalle, Testaccio, Trastevere, Monteverde e tutta la provincia di Roma.
              </p>
            </div>
          </div>
        </section>

        <Footer />
        <FloatingWhatsApp />
      </div>
    </>
  );
};

export default AssistenzaCondizionatoriRoma;
